import { useMemo, type ReactNode } from "react";
import { IconMapPinSingle } from "@/assets/icons/icon-map-pin-single";
import { IconMapPinMultiple } from "@/assets/icons/icon-map-pin-multiple";
import { Marker } from "./MapCanvas";
import type { SpaceCluster } from "../hooks/useSpaceClusters";

/** Classic svg pin marker (single or counted multiple) with highlight glow,
 * pulse animation and a balloon slot shown while highlighted. */
export function SpacePinMarker({
  cluster,
  isHighlighted,
  isPulsing,
  onClick,
  pinHtml: pinHtmlOverride,
  glowColor = "rgba(249,225,11,0.8)",
  children,
}: {
  cluster: SpaceCluster;
  isHighlighted: boolean;
  isPulsing: boolean;
  onClick: () => void;
  /** Overrides the default teardrop/counted pin with this raw SVG markup —
   * e.g. the details drawer's own Woba-branded pin. */
  pinHtml?: string;
  /** Drop-shadow color behind a highlighted pin. Defaults to the brand
   * yellow glow; the details drawer's single-pin map overrides it with a
   * neutral gray. */
  glowColor?: string;
  children?: ReactNode;
}) {
  // The icon factories build multi-KB svg strings; don't rebuild them on
  // every camera frame — the inputs domain is tiny (count, pulsing).
  const count = cluster.members.length;
  const pinHtml = useMemo(
    () =>
      pinHtmlOverride ??
      (count > 1
        ? IconMapPinMultiple(count, isPulsing)
        : IconMapPinSingle(isPulsing)),
    [pinHtmlOverride, count, isPulsing],
  );

  return (
    <Marker
      longitude={cluster.lng}
      latitude={cluster.lat}
      anchor="bottom"
      onClick={(e) => {
        e.originalEvent.stopPropagation();
        onClick();
      }}
      style={{ cursor: "pointer", zIndex: 2 }}
    >
      <div
        className="relative"
        style={{
          filter: isHighlighted
            ? `drop-shadow(0 0 10px ${glowColor})`
            : undefined,
          transform: isHighlighted ? "scale(1.2)" : undefined,
          transition: "filter 0.3s ease-in-out, transform 0.3s ease-in-out",
        }}
      >
        <div
          dangerouslySetInnerHTML={{ __html: pinHtml }}
          style={{ lineHeight: 0 }}
        />
        {isHighlighted && children}
      </div>
    </Marker>
  );
}
